import { PaginatedRequest } from '../types/api';
import {
  FetchAccumulatedAumReportResponse,
  FetchAlpacaJournalResponse,
  FetchAumReportResponse,
  FetchCashReportResponse,
  FetchCashTransactionsReportResponse,
  FetchCmaUsersReportResponse,
  FetchFeeCalculationsReportResponse,
  FetchFinanceUsersReportResponse,
  FetchOrderFxRevenueReportResponse,
  FetchOrdersReportResponse,
  FetchPortfolioCompositionReportResponse,
  FetchPortfolioDetailsReportResponse,
  FetchPortfolioOrderExecutionsReportResponse,
  FetchPortfolioOrdersDataReportResponse,
  FetchPortfolioSnapshotReportResponse,
  FetchPortfolioTotalsReportResponse,
  FetchSystemCashReportResponse,
  FetchSystemUnitsReportResponse,
  FetchTransferFxRevenueReportResponse,
  FetchUnitReportResponse,
  FetchUserIndexFundReportResponse,
  FetchUserDetailsReportResponse,
  FetchUsersReportResponse,
  ReconcileSystemUnitsReportRequest,
  ReconcileSystemUnitsReportResponse,
  ReportApiDateRangeQuery,
} from '../types/report';
import { toQueryParams } from '../utils/request';
import { ApiService } from './common/ApiService';

class ReportService extends ApiService {
  pathname = 'admin';

  fetchPortfolioOrderExecutionReport = async (data: PaginatedRequest) =>
    this.get<FetchPortfolioOrderExecutionsReportResponse>(
      `${this.pathname}/report/portfolio-order-execution?${toQueryParams(data)}`,
    );

  fetchCashReport = async (data: PaginatedRequest) =>
    this.get<FetchCashReportResponse>(`${this.pathname}/report/cash?${toQueryParams(data)}`);

  fetchCashTransactionsReport = async (data: PaginatedRequest) =>
    this.get<FetchCashTransactionsReportResponse>(`${this.pathname}/report/cash-transactions?${toQueryParams(data)}`);

  exportCashTransactionsReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/cash-transactions/export`, { responseType: 'blob' });

  fetchUsersReport = async (data: PaginatedRequest) =>
    this.get<FetchUsersReportResponse>(`${this.pathname}/report/users?${toQueryParams(data)}`);

  exportUsersReportCsv = async () => this.get<Blob>(`${this.pathname}/report/users/export`, { responseType: 'blob' });

  fetchOrdersReport = async (data: PaginatedRequest) =>
    this.get<FetchOrdersReportResponse>(`${this.pathname}/report/orders?${toQueryParams(data)}`);

  exportOrdersReportCsv = async () => this.get<Blob>(`${this.pathname}/report/orders/export`, { responseType: 'blob' });

  fetchAumReport = async (data: PaginatedRequest) =>
    this.get<FetchAumReportResponse>(`${this.pathname}/report/aum?${toQueryParams(data)}`);

  exportAumReportCsv = async () => this.get<Blob>(`${this.pathname}/report/aum/export`, { responseType: 'blob' });

  fetchAccumulatedAumReport = async (data: PaginatedRequest) =>
    this.get<FetchAccumulatedAumReportResponse>(`${this.pathname}/report/accumulated-aum?${toQueryParams(data)}`);

  exportAccumulatedAumReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/accumulated-aum/export`, { responseType: 'blob' });

  fetchUserDetailsReport = async (data: PaginatedRequest) =>
    this.get<FetchUserDetailsReportResponse>(`${this.pathname}/report/user-details?${toQueryParams(data)}`);

  exportUserDetailsReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/user-details/export`, { responseType: 'blob' });

  fetchFeeCalculationsReport = async (data: PaginatedRequest) =>
    this.get<FetchFeeCalculationsReportResponse>(`${this.pathname}/report/fee-calculations?${toQueryParams(data)}`);

  exportFeeCalculationsReportCsv = async (range: ReportApiDateRangeQuery) =>
    this.get<Blob>(`${this.pathname}/report/fee-calculations/export?${toQueryParams(range)}`, {
      responseType: 'blob',
      timeout: 120_000,
    });

  fetchFinanceUsersReport = async (data: PaginatedRequest) =>
    this.get<FetchFinanceUsersReportResponse>(`${this.pathname}/report/finance-users?${toQueryParams(data)}`);

  exportFinanceUsersReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/finance-users/export`, { responseType: 'blob' });

  fetchPortfolioCompositionReport = async (data: PaginatedRequest) =>
    this.get<FetchPortfolioCompositionReportResponse>(
      `${this.pathname}/report/portfolio-composition?${toQueryParams(data)}`,
    );

  exportPortfolioCompositionReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/portfolio-composition/export`, { responseType: 'blob' });

  fetchPortfolioSnapshotReport = async (data: PaginatedRequest) =>
    this.get<FetchPortfolioSnapshotReportResponse>(`${this.pathname}/report/portfolio-snapshot?${toQueryParams(data)}`);

  // The full history is ~80 MB: keeps the client's default 5-minute timeout instead of the 120 s used above
  exportPortfolioSnapshotReportCsv = async (range: ReportApiDateRangeQuery) =>
    this.get<Blob>(`${this.pathname}/report/portfolio-snapshot/export?${toQueryParams(range)}`, {
      responseType: 'blob',
    });

  fetchPortfolioOrdersDataReport = async (data: PaginatedRequest) =>
    this.get<FetchPortfolioOrdersDataReportResponse>(
      `${this.pathname}/report/portfolio-orders-data?${toQueryParams(data)}`,
    );

  exportPortfolioOrdersDataReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/portfolio-orders-data/export`, { responseType: 'blob' });

  fetchPortfolioDetailsReport = async (data: PaginatedRequest) =>
    this.get<FetchPortfolioDetailsReportResponse>(`${this.pathname}/report/portfolio-details?${toQueryParams(data)}`);

  exportPortfolioDetailsReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/portfolio-details/export`, { responseType: 'blob' });

  fetchPortfolioTotalsReport = async (data: PaginatedRequest) =>
    this.get<FetchPortfolioTotalsReportResponse>(`${this.pathname}/report/portfolio-totals?${toQueryParams(data)}`);

  exportPortfolioTotalsReportCsv = async () =>
    this.get<Blob>(`${this.pathname}/report/portfolio-totals/export`, {
      responseType: 'blob',
      timeout: 120_000,
    });

  fetchSystemCashReport = async (data: PaginatedRequest) =>
    this.get<FetchSystemCashReportResponse>(`${this.pathname}/report/cash/system?${toQueryParams(data)}`);

  fetchUnitsReport = async (data: PaginatedRequest) =>
    this.get<FetchUnitReportResponse>(`${this.pathname}/report/units?${toQueryParams(data)}`);

  fetchSystemUnitsReport = async () => this.get<FetchSystemUnitsReportResponse>(`${this.pathname}/report/units/system`);

  reconcileSystemUnitsReport = async ({ id, ...systemReport }: ReconcileSystemUnitsReportRequest) =>
    this.put<ReconcileSystemUnitsReportResponse, Omit<ReconcileSystemUnitsReportRequest, 'id'>>(
      `${this.pathname}/report/units/${id}/reconciliation`,
      systemReport,
    );

  fetchFxRevenueAlpacaOrdersReport = async (data: PaginatedRequest) =>
    this.get<FetchOrderFxRevenueReportResponse>(`${this.pathname}/alpaca/report/orders?${toQueryParams(data)}`);

  fetchFxRevenueAlpacaTransfersReport = async (data: PaginatedRequest) =>
    this.get<FetchTransferFxRevenueReportResponse>(`${this.pathname}/alpaca/report/transfers?${toQueryParams(data)}`);

  fetchFxRevenueJadwaOrdersReport = async (data: PaginatedRequest) =>
    this.get<FetchOrderFxRevenueReportResponse>(`${this.pathname}/jadwa/report/orders?${toQueryParams(data)}`);

  fetchFxRevenueJadwaTransfersReport = async (data: PaginatedRequest) =>
    this.get<FetchTransferFxRevenueReportResponse>(`${this.pathname}/jadwa/report/transfers?${toQueryParams(data)}`);

  fetchUserIndexFundReport = async (
    data: PaginatedRequest & {
      startDate?: string;
      endDate?: string;
      groupByUser: boolean;
    },
  ) => this.get<FetchUserIndexFundReportResponse>(`${this.pathname}/user-index-fund/report?${toQueryParams(data)}`);

  fetchAlpacaJournalReport = async (data: PaginatedRequest) =>
    this.get<FetchAlpacaJournalResponse>(`${this.pathname}/alpaca/report/journal?${toQueryParams(data)}`);

  fetchCmaUsersReport = async (data: PaginatedRequest) =>
    this.get<FetchCmaUsersReportResponse>(`${this.pathname}/report/cma-users?${toQueryParams(data)}`);

  exportCmaUsersReportCsv = async () => this.get<Blob>(`${this.pathname}/report/cma-users`, { responseType: 'blob' });
}

export const reportService = new ReportService();
